/**
 * ProducerGallery — Thumbnail strip matching PlaceCoreGalleryStrip style.
 * Small h-16/h-20 thumbnails, no section wrapper, no heading.
 * Supports syncing with hero carousel via activeHeroIndex + onSelectIndex.
 */
import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useGalleryAssets } from '@/hooks/useGalleryAssets';
import { cn } from '@/lib/utils';

interface ProducerGalleryProps {
  producerId: string;
  producerName: string;
  maxItems?: number;
  hideTitle?: boolean;
  /** Currently active hero image index (for highlight sync). */
  activeHeroIndex?: number;
  /** Called when user taps a thumbnail to jump hero to that image. */
  onSelectIndex?: (index: number) => void;
}

export function ProducerGallery({ producerId, producerName, maxItems = 5, activeHeroIndex, onSelectIndex }: ProducerGalleryProps) {
  const { urls320, urls640 } = useGalleryAssets('producer', producerId);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  if (urls320.length < 2) return null;

  const totalCount = urls320.length;
  const limit = Math.min(maxItems, 6);
  const showOverlay = totalCount > limit;
  const visibleThumbs = showOverlay ? urls320.slice(0, limit - 1) : urls320.slice(0, limit);
  const extraCount = totalCount - (limit - 1);

  const prev = () =>
    setSelectedIdx((i) => (i !== null ? (i - 1 + totalCount) % totalCount : 0));
  const next = () =>
    setSelectedIdx((i) => (i !== null ? (i + 1) % totalCount : 0));

  const handleThumbClick = (index: number) => {
    if (onSelectIndex) {
      // Mobile: jump the hero carousel
      onSelectIndex(index);
    } else {
      // Desktop: open lightbox
      setSelectedIdx(index);
    }
  };

  return (
    <>
      <div className="flex gap-2">
        {visibleThumbs.map((url, i) => (
          <button
            key={url}
            onClick={() => handleThumbClick(i)}
            className={cn(
              "shrink-0 rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity",
              activeHeroIndex !== undefined && activeHeroIndex !== i && "opacity-60",
              activeHeroIndex === i && "ring-2 ring-primary/50",
            )}
          >
            <img
              src={url}
              alt={`${producerName} bild ${i + 1}`}
              className="h-16 sm:h-20 w-auto aspect-[4/3] object-cover"
              loading="lazy"
            />
          </button>
        ))}

        {showOverlay && (
          <button
            onClick={() => handleThumbClick(limit - 1)}
            className={cn(
              "shrink-0 rounded-lg overflow-hidden relative focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity",
              activeHeroIndex !== undefined && activeHeroIndex !== (limit - 1) && "opacity-60",
              activeHeroIndex === (limit - 1) && "ring-2 ring-primary/50",
            )}
          >
            <img
              src={urls320[limit - 1]}
              alt={`${producerName} bild ${limit}`}
              className="h-16 sm:h-20 w-auto aspect-[4/3] object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="text-white text-sm font-medium">+{extraCount} bilder</span>
            </div>
          </button>
        )}
      </div>

      {/* Lightbox — uses 640 variants */}
      <Dialog open={selectedIdx !== null} onOpenChange={() => setSelectedIdx(null)}>
        <DialogContent className="max-w-3xl p-0 bg-black border-none">
          {selectedIdx !== null && (
            <div className="relative flex items-center justify-center min-h-[50vh]">
              <img
                src={urls640[selectedIdx] || urls320[selectedIdx]}
                alt={`${producerName} bild ${selectedIdx + 1}`}
                className="max-h-[80vh] w-auto max-w-full object-contain"
              />
              {totalCount > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70"
                    aria-label="Föregående bild"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70"
                    aria-label="Nästa bild"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/70 text-xs">
                {selectedIdx + 1} / {totalCount}
              </span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
